import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft, Trophy, Target, Play, CheckCircle, Calendar, 
  Users, Award, Sparkles, ChevronRight, Clock, X
} from 'lucide-react';
import { doc, getDoc, updateDoc, collection, addDoc, onSnapshot, query, where, orderBy, getDocs } from 'firebase/firestore';
import { db } from '../../config/firebase';

const PlayoffEnginePage = () => {
  const { leagueId } = useParams();
  const navigate = useNavigate();
  const [league, setLeague] = useState(null);
  const [teams, setTeams] = useState([]);
  const [standings, setStandings] = useState([]);
  const [playoffFixtures, setPlayoffFixtures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [playoffsGenerated, setPlayoffsGenerated] = useState(false);

  useEffect(() => {
    const fetchLeague = async () => {
      try {
        const leagueDoc = await getDoc(doc(db, 'leagues', leagueId));
        if (leagueDoc.exists()) {
          const leagueData = { id: leagueDoc.id, ...leagueDoc.data() };
          setLeague(leagueData);
          setPlayoffsGenerated(leagueData.playoffsGenerated || false);
        } else {
          setError('League not found');
        }
      } catch (err) {
        setError('Failed to load league details');
        console.error('Error fetching league:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchLeague();
  }, [leagueId]);

  useEffect(() => {
    const teamsQuery = query(
      collection(db, 'teams'),
      orderBy('points', 'desc')
    );

    const unsubscribeTeams = onSnapshot(teamsQuery, (snapshot) => {
      const teamsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setTeams(teamsData);
      calculateStandings(teamsData);
    });

    return unsubscribeTeams;
  }, []);

  useEffect(() => {
    // Fetch playoff fixtures
    const fixturesQuery = query(
      collection(db, 'fixtures'),
      where('leagueId', '==', leagueId),
      where('isPlayoff', '==', true)
    );

    const unsubscribeFixtures = onSnapshot(fixturesQuery, (snapshot) => {
      const fixturesData = snapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
        .filter(fixture => fixture.status !== 'deleted'); // Filter out deleted fixtures
      setPlayoffFixtures(fixturesData);
    });

    return unsubscribeFixtures;
  }, [leagueId]);

  const calculateStandings = (teamsData) => {
    const sortedTeams = teamsData
      .sort((a, b) => {
        if (b.points !== a.points) return b.points - a.points;
        if (b.wins !== a.wins) return b.wins - a.wins;
        const aSetDiff = (a.setsFor || 0) - (a.setsAgainst || 0);
        const bSetDiff = (b.setsFor || 0) - (b.setsAgainst || 0);
        return bSetDiff - aSetDiff;
      })
      .map((team, index) => ({
        ...team,
        position: index + 1
      }));
    
    setStandings(sortedTeams);
  };

  const generatePlayoffFixtures = async () => {
    if (standings.length < 4) {
      setError('Need at least 4 teams for playoffs');
      return;
    }

    setSaving(true);
    setError('');

    try {
      const top4Teams = standings.slice(0, 4);
      const playoffDate = new Date();
      playoffDate.setDate(playoffDate.getDate() + 7); // 1 week from now
      const baseDateStr = playoffDate.toISOString().split('T')[0];
      
      const allFixtures = [];
      
      // Helper function to create 4 fixtures (one per match) for a playoff round
      const createPlayoffRoundFixtures = (homeTeam, homeTeamId, awayTeam, awayTeamId, round, date, time, roundNumber) => {
        const fixtures = [];
        for (let matchNum = 1; matchNum <= 4; matchNum++) {
          fixtures.push({
            leagueId,
            isPlayoff: true,
            playoffRound: round,
            round: `Playoff - ${round}`,
            homeTeam,
            homeTeamId,
            awayTeam,
            awayTeamId,
            date,
            time,
            court: 'Court 1',
            status: 'scheduled',
            matchNumber: matchNum,
            gameNumber: matchNum,
            roundNumber,
            createdAt: new Date().toISOString()
          });
        }
        return fixtures;
      };
      
      // Eliminator: 3rd vs 4th (4 fixtures)
      allFixtures.push(...createPlayoffRoundFixtures(
        top4Teams[2].name,
        top4Teams[2].id,
        top4Teams[3].name,
        top4Teams[3].id,
        'Eliminator',
        baseDateStr,
        '18:00',
        1
      ));

      // Qualifier 1: 1st vs 2nd (4 fixtures)
      const q1Date = new Date(playoffDate);
      q1Date.setDate(q1Date.getDate() + 1);
      allFixtures.push(...createPlayoffRoundFixtures(
        top4Teams[0].name,
        top4Teams[0].id,
        top4Teams[1].name,
        top4Teams[1].id,
        'Qualifier 1',
        q1Date.toISOString().split('T')[0],
        '18:00',
        2
      ));

      // Qualifier 2: TBD teams (4 fixtures - will be updated after Eliminator and Q1 complete)
      const q2Date = new Date(q1Date);
      q2Date.setDate(q2Date.getDate() + 2);
      for (let matchNum = 1; matchNum <= 4; matchNum++) {
        allFixtures.push({
          leagueId,
          isPlayoff: true,
          playoffRound: 'Qualifier 2',
          round: 'Playoff - Qualifier 2',
          homeTeam: 'TBD (Loser of Q1)',
          homeTeamId: null,
          awayTeam: 'TBD (Winner of Eliminator)',
          awayTeamId: null,
          date: q2Date.toISOString().split('T')[0],
          time: '18:00',
          court: 'Court 1',
          status: 'scheduled',
          matchNumber: matchNum,
          gameNumber: matchNum,
          roundNumber: 3,
          dependsOn: ['Eliminator', 'Qualifier 1'],
          createdAt: new Date().toISOString()
        });
      }

      // Finals: TBD teams (4 fixtures - will be updated after Q1 and Q2 complete)
      const finalsDate = new Date(q2Date);
      finalsDate.setDate(finalsDate.getDate() + 3);
      for (let matchNum = 1; matchNum <= 4; matchNum++) {
        allFixtures.push({
          leagueId,
          isPlayoff: true,
          playoffRound: 'Finals',
          round: 'Playoff - Finals',
          homeTeam: 'TBD (Winner of Q1)',
          homeTeamId: null,
          awayTeam: 'TBD (Winner of Q2)',
          awayTeamId: null,
          date: finalsDate.toISOString().split('T')[0],
          time: '19:00',
          court: 'Court 1',
          status: 'scheduled',
          matchNumber: matchNum,
          gameNumber: matchNum,
          roundNumber: 4,
          dependsOn: ['Qualifier 1', 'Qualifier 2'],
          createdAt: new Date().toISOString()
        });
      }

      // Save all 16 playoff fixtures (4 rounds × 4 matches)
      const savePromises = allFixtures.map(fixture => addDoc(collection(db, 'fixtures'), fixture));
      await Promise.all(savePromises);

      // Update league status
      await updateDoc(doc(db, 'leagues', leagueId), {
        playoffsGenerated: true,
        playoffsActive: true,
        updatedAt: new Date().toISOString()
      });

      setPlayoffsGenerated(true);
      console.log(`✅ Generated ${allFixtures.length} playoff fixtures (4 rounds × 4 matches each)`);

    } catch (error) {
      setError('Failed to generate playoff fixtures');
      console.error('Error generating playoff fixtures:', error);
    } finally {
      setSaving(false);
    }
  };

  const deleteAllPlayoffFixtures = async () => {
    if (!window.confirm('⚠️ This will delete ALL playoff fixtures. This action cannot be undone. Continue?')) {
      return;
    }

    setSaving(true);
    setError('');

    try {
      // Get all playoff fixtures for this league
      const playoffFixturesQuery = query(
        collection(db, 'fixtures'),
        where('leagueId', '==', leagueId),
        where('isPlayoff', '==', true)
      );
      
      const playoffFixturesSnapshot = await getDocs(playoffFixturesQuery);
      
      // Delete all playoff fixtures
      const deletePromises = playoffFixturesSnapshot.docs.map(doc => 
        updateDoc(doc.ref, { status: 'deleted' })
      );
      
      await Promise.all(deletePromises);

      // Update league status
      await updateDoc(doc(db, 'leagues', leagueId), {
        playoffsGenerated: false,
        playoffsActive: false,
        updatedAt: new Date().toISOString()
      });

      setPlayoffsGenerated(false);
      setPlayoffFixtures([]);
      console.log(`✅ Deleted ${playoffFixturesSnapshot.docs.length} playoff fixtures`);

    } catch (error) {
      setError('Failed to delete playoff fixtures');
      console.error('Error deleting playoff fixtures:', error);
    } finally {
      setSaving(false);
    }
  };

  const getPlayoffRounds = () => {
    console.log('🔍 All playoff fixtures:', playoffFixtures.length);
    console.log('Playoff fixtures data:', playoffFixtures.map(f => ({
      round: f.playoffRound,
      status: f.status,
      home: f.homeTeam,
      away: f.awayTeam
    })));
    
    const eliminator = playoffFixtures.filter(f => f.playoffRound === 'Eliminator');
    const q1 = playoffFixtures.filter(f => f.playoffRound === 'Qualifier 1');
    const q2 = playoffFixtures.filter(f => f.playoffRound === 'Qualifier 2');
    const finals = playoffFixtures.filter(f => f.playoffRound === 'Finals');

    console.log(`Eliminator: ${eliminator.length}, Q1: ${q1.length}, Q2: ${q2.length}, Finals: ${finals.length}`);

    return { eliminator, q1, q2, finals };
  };

  const rounds = getPlayoffRounds();

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          >
            <Trophy size={48} style={{ margin: '0 auto 1rem', color: '#10B981' }} />
          </motion.div>
          <p style={{ color: '#6B7280', fontWeight: '600' }}>Loading playoffs...</p>
        </div>
      </div>
    );
  }

  if (error && !league) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '1rem', color: '#1C563D' }}>
            {error}
          </h2>
          <button
            onClick={() => navigate('/league')}
            style={{
              padding: '0.75rem 1.5rem',
              background: 'linear-gradient(135deg, #1C563D 0%, #2D7A5A 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '0.5rem',
              cursor: 'pointer',
              fontWeight: '600'
            }}
          >
            Back to Leagues
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #FEF3C7 0%, #FFFFFF 50%, #DBEAFE 100%)' }}>
      <div className="container mx-auto px-4 py-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          style={{ marginBottom: '2rem' }}
        >
          <button
            onClick={() => navigate(`/league/${leagueId}/leaderboard`)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              color: '#6B7280',
              marginBottom: '1rem',
              fontSize: '0.875rem',
              fontWeight: '600',
              background: 'none',
              border: 'none',
              cursor: 'pointer'
            }}
          >
            <ArrowLeft size={20} />
            Back to Leaderboard
          </button>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
            <div style={{
              width: '64px',
              height: '64px',
              background: 'linear-gradient(135deg, #FCD34D 0%, #F59E0B 100%)',
              borderRadius: '1rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 8px 16px rgba(252, 211, 77, 0.4)'
            }}>
              <Trophy size={32} style={{ color: 'white' }} />
            </div>
            <div>
              <h1 style={{ fontSize: 'clamp(2rem, 5vw, 3rem)', fontWeight: 'bold', marginBottom: '0.25rem', color: '#1C563D' }}>
                Playoff Engine
              </h1>
              <p style={{ color: '#6B7280', fontSize: '1.125rem' }}>
                {league?.name}
              </p>
            </div>
          </div>
        </motion.div>

        {/* Top 4 Standings Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          style={{
            background: 'white',
            borderRadius: '1.5rem',
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
            padding: '2rem',
            marginBottom: '2rem',
            border: '1px solid rgba(0, 0, 0, 0.05)'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.5rem' }}>
            <div style={{
              width: '48px',
              height: '48px',
              background: 'linear-gradient(135deg, #DBEAFE 0%, #93C5FD 100%)',
              borderRadius: '0.75rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Target size={24} style={{ color: '#1E40AF' }} />
            </div>
            <div>
              <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#1C563D' }}>
                Playoff Qualifiers
              </h2>
              <p style={{ fontSize: '0.875rem', color: '#6B7280' }}>
                Top 4 teams qualify for playoffs
              </p>
            </div>
          </div>

          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
            gap: '1rem' 
          }}>
            {standings.slice(0, 4).map((team, index) => (
              <motion.div
                key={team.id}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.1 + index * 0.1 }}
                style={{
                  background: index === 0 ? 'linear-gradient(135deg, #FEF3C7 0%, #FCD34D 100%)' :
                             index === 1 ? 'linear-gradient(135deg, #E5E7EB 0%, #D1D5DB 100%)' :
                             index === 2 ? 'linear-gradient(135deg, #FED7AA 0%, #FDBA74 100%)' :
                             'linear-gradient(135deg, #DBEAFE 0%, #93C5FD 100%)',
                  borderRadius: '1rem',
                  padding: '1.5rem',
                  border: '2px solid',
                  borderColor: index === 0 ? '#F59E0B' :
                              index === 1 ? '#9CA3AF' :
                              index === 2 ? '#F97316' :
                              '#3B82F6',
                  position: 'relative',
                  overflow: 'hidden'
                }}
              >
                <div style={{
                  position: 'absolute',
                  top: '-20px',
                  right: '-20px',
                  width: '80px',
                  height: '80px',
                  borderRadius: '50%',
                  background: 'rgba(255, 255, 255, 0.2)',
                  backdropFilter: 'blur(10px)'
                }} />
                
                <div style={{ position: 'relative', zIndex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
                    <div style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      background: 'rgba(255, 255, 255, 0.9)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 'bold',
                      fontSize: '1.25rem',
                      color: index === 0 ? '#F59E0B' :
                            index === 1 ? '#6B7280' :
                            index === 2 ? '#F97316' :
                            '#3B82F6',
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                    }}>
                      #{team.position}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 'bold', fontSize: '1.125rem', color: '#1F2937' }}>
                        {team.name}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: '#6B7280', fontWeight: '600' }}>
                        {team.points || 0} pts • {team.wins || 0}W-{team.losses || 0}L
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Generate Playoffs or Show Bracket */}
        {!playoffsGenerated ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            style={{
              background: 'white',
              borderRadius: '1.5rem',
              padding: '4rem 2rem',
              textAlign: 'center',
              boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
            }}
          >
            <div style={{
              width: '96px',
              height: '96px',
              background: 'linear-gradient(135deg, #FCD34D 0%, #F59E0B 100%)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1.5rem',
              boxShadow: '0 8px 16px rgba(252, 211, 77, 0.4)'
            }}>
              <Sparkles size={48} style={{ color: 'white' }} />
            </div>
            
            <h3 style={{ fontSize: '1.75rem', fontWeight: 'bold', color: '#1C563D', marginBottom: '0.75rem' }}>
              Generate Playoff Fixtures
            </h3>
            <p style={{ color: '#6B7280', fontSize: '1.125rem', marginBottom: '2rem', maxWidth: '600px', margin: '0 auto 2rem' }}>
              Create elimination playoffs with the top 4 teams. Fixtures will be added to your league schedule and can be scored using the live scoring system.
            </p>
            
            <div style={{
              background: '#FEF3C7',
              border: '2px solid #FCD34D',
              borderRadius: '1rem',
              padding: '1.5rem',
              marginBottom: '2rem',
              maxWidth: '600px',
              margin: '0 auto 2rem'
            }}>
              <div style={{ fontWeight: '700', color: '#92400E', marginBottom: '0.75rem', fontSize: '1rem' }}>
                Playoff Format:
              </div>
              <div style={{ color: '#92400E', fontSize: '0.875rem', textAlign: 'left' }}>
                <div style={{ marginBottom: '0.5rem' }}>
                  ✓ <strong>Eliminator:</strong> 3rd vs 4th place
                </div>
                <div style={{ marginBottom: '0.5rem' }}>
                  ✓ <strong>Qualifier 1:</strong> 1st vs 2nd place
                </div>
                <div style={{ marginBottom: '0.5rem' }}>
                  ✓ <strong>Qualifier 2:</strong> Loser of Q1 vs Winner of Eliminator
                </div>
                <div>
                  ✓ <strong>Finals:</strong> Winner of Q1 vs Winner of Q2
                </div>
              </div>
            </div>

            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={generatePlayoffFixtures}
              disabled={saving || standings.length < 4}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.75rem',
                padding: '1rem 2rem',
                background: (saving || standings.length < 4) ? '#9CA3AF' : 'linear-gradient(135deg, #FCD34D 0%, #F59E0B 100%)',
                color: (saving || standings.length < 4) ? '#F3F4F6' : '#92400E',
                border: 'none',
                borderRadius: '0.75rem',
                cursor: (saving || standings.length < 4) ? 'not-allowed' : 'pointer',
                fontWeight: '700',
                fontSize: '1.125rem',
                boxShadow: (saving || standings.length < 4) ? 'none' : '0 8px 16px rgba(252, 211, 77, 0.4)'
              }}
            >
              <Trophy size={24} />
              {saving ? 'Generating Playoffs...' : 'Generate Playoff Fixtures'}
            </motion.button>
            
            {standings.length < 4 && (
              <p style={{ color: '#EF4444', marginTop: '1rem', fontSize: '0.875rem', fontWeight: '600' }}>
                Need at least 4 teams to generate playoffs
              </p>
            )}
          </motion.div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            {/* Playoff Bracket Visualization */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              style={{
                background: 'white',
                borderRadius: '1.5rem',
                padding: '2rem',
                boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
              }}
            >
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between', 
                marginBottom: '2rem',
                flexWrap: 'wrap',
                gap: '1rem'
              }}>
                <h2 style={{ 
                  fontSize: '1.75rem', 
                  fontWeight: 'bold', 
                  color: '#1C563D', 
                  margin: 0,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem'
                }}>
                  <Award size={28} style={{ color: '#F59E0B' }} />
                  Playoff Bracket
                </h2>
                
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={deleteAllPlayoffFixtures}
                  disabled={saving}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.75rem 1.5rem',
                    background: saving ? '#9CA3AF' : 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '0.5rem',
                    cursor: saving ? 'not-allowed' : 'pointer',
                    fontWeight: '700',
                    fontSize: '0.875rem',
                    boxShadow: saving ? 'none' : '0 4px 8px rgba(239, 68, 68, 0.4)'
                  }}
                >
                  <X size={18} />
                  {saving ? 'Deleting...' : 'Delete All Playoffs'}
                </motion.button>
              </div>

              {/* Playoff Rounds */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                {/* Eliminator */}
                {rounds.eliminator.map(fixture => (
                  <PlayoffFixtureCard 
                    key={fixture.id} 
                    fixture={fixture} 
                    roundName="Eliminator"
                    roundColor="#EF4444"
                    navigate={navigate}
                  />
                ))}

                {/* Qualifier 1 */}
                {rounds.q1.map(fixture => (
                  <PlayoffFixtureCard 
                    key={fixture.id} 
                    fixture={fixture} 
                    roundName="Qualifier 1"
                    roundColor="#F59E0B"
                    navigate={navigate}
                  />
                ))}

                {/* Qualifier 2 */}
                {rounds.q2.map(fixture => (
                  <PlayoffFixtureCard 
                    key={fixture.id} 
                    fixture={fixture} 
                    roundName="Qualifier 2"
                    roundColor="#3B82F6"
                    navigate={navigate}
                  />
                ))}

                {/* Finals */}
                {rounds.finals.map(fixture => (
                  <PlayoffFixtureCard 
                    key={fixture.id} 
                    fixture={fixture} 
                    roundName="FINALS"
                    roundColor="#10B981"
                    navigate={navigate}
                  />
                ))}
              </div>
            </motion.div>
          </div>
        )}

        {error && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            style={{
              background: '#FEE2E2',
              border: '1px solid #FECACA',
              color: '#DC2626',
              padding: '1rem',
              borderRadius: '0.75rem',
              marginTop: '1rem',
              fontWeight: '600'
            }}
          >
            {error}
          </motion.div>
        )}
      </div>
    </div>
  );
};

// Playoff Fixture Card Component
const PlayoffFixtureCard = ({ fixture, roundName, roundColor, navigate }) => {
  const isTBD = fixture.homeTeam.includes('TBD') || fixture.awayTeam.includes('TBD');
  
  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      style={{
        background: `linear-gradient(135deg, ${roundColor}15 0%, ${roundColor}05 100%)`,
        border: `2px solid ${roundColor}40`,
        borderRadius: '1rem',
        padding: '1.5rem',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* Round Badge */}
      <div style={{
        position: 'absolute',
        top: '-10px',
        right: '20px',
        background: roundColor,
        color: 'white',
        padding: '0.5rem 1rem',
        borderRadius: '0 0 0.5rem 0.5rem',
        fontSize: '0.75rem',
        fontWeight: '700',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        boxShadow: `0 4px 8px ${roundColor}40`
      }}>
        {roundName}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap', marginTop: '1rem' }}>
        {/* Match Info */}
        <div style={{ flex: 1, minWidth: '300px' }}>
          <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#1F2937', marginBottom: '0.5rem' }}>
            {fixture.homeTeam} <span style={{ color: '#6B7280', fontSize: '1.25rem' }}>vs</span> {fixture.awayTeam}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap', fontSize: '0.875rem', color: '#6B7280' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Calendar size={16} />
              {new Date(fixture.date).toLocaleDateString('en-US', { 
                month: 'short', 
                day: 'numeric', 
                year: 'numeric' 
              })}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Clock size={16} />
              {fixture.time}
            </div>
            <div>
              {fixture.court}
            </div>
          </div>
        </div>

        {/* Status & Actions */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <span style={{
            padding: '0.5rem 1rem',
            borderRadius: '0.5rem',
            fontSize: '0.875rem',
            fontWeight: '700',
            textTransform: 'uppercase',
            background: fixture.status === 'completed' ? '#D1FAE5' :
                       fixture.status === 'live' ? '#FEE2E2' :
                       '#DBEAFE',
            color: fixture.status === 'completed' ? '#065F46' :
                  fixture.status === 'live' ? '#DC2626' :
                  '#1E40AF'
          }}>
            {fixture.status === 'completed' ? '✓ Completed' :
             fixture.status === 'live' ? '● Live' :
             isTBD ? 'TBD' : 'Scheduled'}
          </span>

          {!isTBD && fixture.status === 'scheduled' && (
            <Link
              to={`/match/${fixture.id}/scoring`}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.75rem 1.25rem',
                background: roundColor,
                color: 'white',
                textDecoration: 'none',
                borderRadius: '0.5rem',
                fontWeight: '700',
                fontSize: '0.875rem',
                boxShadow: `0 4px 8px ${roundColor}40`,
                transition: 'all 0.2s'
              }}
            >
              <Play size={16} />
              Start Match
            </Link>
          )}
          
          {fixture.status === 'live' && (
            <Link
              to={`/match/${fixture.id}/scoring`}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.75rem 1.25rem',
                background: '#DC2626',
                color: 'white',
                textDecoration: 'none',
                borderRadius: '0.5rem',
                fontWeight: '700',
                fontSize: '0.875rem',
                boxShadow: '0 4px 8px rgba(220, 38, 38, 0.4)',
                animation: 'pulse 2s infinite'
              }}
            >
              <Play size={16} />
              Continue Match
            </Link>
          )}

          {fixture.status === 'completed' && fixture.winner && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.75rem 1.25rem',
              background: '#10B981',
              color: 'white',
              borderRadius: '0.5rem',
              fontWeight: '700',
              fontSize: '0.875rem'
            }}>
              <Trophy size={16} />
              Winner: {fixture.winner}
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default PlayoffEnginePage;
